import { ChangeEvent, RefObject, SyntheticEvent, useEffect, useRef, useState } from "react"
import { Alert, Button, Form, Modal, Spinner, Table } from "react-bootstrap"
import EventTicketService, { IEventTicket, IEventTicketCustomerUpdate } from "../../services/EventTicketService"
import Utils from "../../utils/Utils"
import moment from "moment"
import Configs from "../../Configs"
import QRCodeModal from "../../components/QRCodeModal"
import QRCodeService from "../../services/QRCodeService"
import { useTranslation } from "react-i18next"
import PhoneInput, { CountryData } from "react-phone-input-2"
import PaymentStatusBadge from "../../components/PaymentStatusBadge"

interface IEventTicketCustomerEditModalProps {
    providerId: number | null
    eventTicketId: number | null
    show: boolean
    onClose: () => void
    onSuccess: () => void
}

function EventTicketCustomerEditModal(props: IEventTicketCustomerEditModalProps) {
    const { t } = useTranslation("global")
    const [message, setMessage] = useState("")
    const [processing, setProcessing] = useState<boolean>(false)
    const [validatedForm, setValidatedForm] = useState(false)
    const formRef = useRef<HTMLFormElement>(null)

    const [eventTicketCustomerUpdate, setEventTicketCustomerUpdate] = useState<IEventTicketCustomerUpdate>()
    const [eventTicket, setEventTicket] = useState<IEventTicket>()

    const GetEventTicket = async () => {
        if (props.providerId && props.eventTicketId && props.show) {
            const res = await EventTicketService.get(props.providerId, props.eventTicketId)

            if (res?.isSuccess) {
                const data: IEventTicket = res.data

                setEventTicket(data)

                setEventTicketCustomerUpdate({
                    note: data.note,
                    customer: {
                        id: data.customer.id,
                        name: data.customer.name,
                        tel: data.customer.tel,
                        formattedTel: data.customer.formattedTel,
                        email: data.customer.email,
                        address: data.customer.address,
                        personalId: data.customer.personalId
                    }
                })
            } else {
                console.log(res?.message)
                props.onClose()
            }
        } else {
            props.onClose()
        }
    }

    useEffect(() => {
        setMessage("")
        GetEventTicket()
    }, [props.providerId, props.show])

    const handleCustomerChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (eventTicketCustomerUpdate) {
            setEventTicketCustomerUpdate({ ...eventTicketCustomerUpdate, customer: { ...eventTicketCustomerUpdate.customer, [e.target.name]: e.target.value } })
        }
    }

    const handleSave = async (e: SyntheticEvent) => {
        e.preventDefault()

        if (!processing) {
            setProcessing(true)
            if (props.providerId && props.eventTicketId && eventTicketCustomerUpdate) {
                if (formRef.current?.checkValidity()) {
                    let updateData = { ...eventTicketCustomerUpdate }
                    updateData.customer.tel = (dialCode === updateData.customer.tel) ? "" : updateData.customer.tel

                    const res = await EventTicketService.updateCustomer(props.providerId, props.eventTicketId, updateData)

                    if (res?.isSuccess) {
                        props.onSuccess()
                        props.onClose()
                        setMessage(res.message)
                    } else {
                        setMessage(res?.message)
                    }

                } else {
                    setValidatedForm(true)
                }
            }
            setProcessing(false)
        }
    }

    // QRCode
    const [selectedText, setSelectedText] = useState("")
    const [qrCode, setQRCode] = useState<{ image: string, code: string, caption?: Array<string> }>({ image: "", code: "", caption: [] })
    const [showQRCodeModal, setShowQRCodeModal] = useState(false)
    const handleCloseQRCodeModal = () => {
        setShowQRCodeModal(false)
        setQRCode({ image: "", code: "", caption: [] })
        setSelectedText("")
    }

    const handleShowQRCodeModal = (text: string) => {
        if (text) {
            const res = QRCodeService.generate(text)

            res.then(data => {
                if (data?.isSuccess) {
                    setQRCode({ image: data.data.image, code: text, caption: [] })
                    setSelectedText(text)
                    setShowQRCodeModal(true)
                } else {
                    console.log(data?.message)
                }
            })
        }
    }

    const handleDownloadQRCode = () => {
        if (selectedText) {
            QRCodeService.download(selectedText)
        }
    }

    //PhoneInput
    const [dialCode, setDialCode] = useState("")
    const handlePhoneInputChange = (value: string, data: {} | CountryData) => {
        if (eventTicketCustomerUpdate) {
            let dialCode = (data as CountryData).dialCode
            setDialCode(dialCode)
            setEventTicketCustomerUpdate({ ...eventTicketCustomerUpdate, customer: { ...eventTicketCustomerUpdate.customer, tel: value } })
        }
    }

    return (<>
        <Modal show={props.show} onHide={props.onClose} backdrop="static" keyboard={false} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{t("eventTicketCustomerEditModal.title")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form noValidate validated={validatedForm} ref={formRef as RefObject<HTMLFormElement>}>
                    {eventTicket &&
                        <fieldset>
                            <legend>{t("eventTicketDetailsModal.ticketDetails")}</legend>
                            <dl className="ticket-details">
                                <dt>{t("eventTicketsPage.event")}:</dt><dd>{eventTicket?.event?.name || ""}</dd>
                                <dt>{t("eventTicketsPage.date")}:</dt><dd>{eventTicket?.eventDate ? moment(eventTicket?.eventDate).format(Configs.DATE_FORMAT) : ""}</dd>
                                <dt>{t("eventTicketsPage.time")}:</dt><dd>{eventTicket?.eventSchedule?.startTime.substring(0, 5)} - {eventTicket?.eventSchedule?.endTime.substring(0, 5)}</dd>
                                <dt>{t("eventTicketsPage.amount")}:</dt><dd>{Utils.formatNumber(eventTicket?.totalPrice || 0)} {eventTicket?.currency}</dd>
                            </dl>
                            <Table responsive bordered size="sm">
                                <thead>
                                    <tr>
                                        <th>{t("eventTicketsPage.code")}</th>
                                        <th>{t("eventTicketsPage.ticketClass")}/{t("eventTicketsPage.seat")}</th>
                                        <th>{t("eventTicketsPage.price")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {eventTicket.details?.map(x => <tr key={x.id}>
                                        <td>
                                            <Button variant="default" size="sm" onClick={() => handleShowQRCodeModal(x.code)}>
                                                <i className="fas fa-qrcode text-info"></i>
                                            </Button>
                                            {x.code || ""}
                                        </td>
                                        <td>{x.ticketClass?.name}{x.seat ? "/" : ""}{x.seat?.letter || ""}{x.seat?.number || ""}</td>
                                        <td>{Utils.formatNumber(x.price || 0)}</td>
                                    </tr>)}
                                </tbody>
                            </Table>
                        </fieldset>}
                    {eventTicketCustomerUpdate && <>
                        <fieldset>
                            <legend>{t("eventTicketDetailsModal.paymentDetails")}</legend>
                            <dl className="ticket-details dt-200">
                                <dt>{t("eventTicketsPage.paymentService")}:</dt><dd>{eventTicket?.paymentService && t(`paymentService.${eventTicket.paymentService}`)}</dd>
                                <dt>{t("eventTicketsPage.paymentStatus")}:</dt><dd><PaymentStatusBadge value={eventTicket?.paymentStatus || ""}></PaymentStatusBadge></dd>
                                <dt>{t("eventTicketsPage.note")}:</dt><dd>
                                    <Form.Control as="textarea" rows={2} type="text" name="note" value={eventTicketCustomerUpdate?.note || ""} onChange={(e) => setEventTicketCustomerUpdate({ ...eventTicketCustomerUpdate, note: e.target.value })} />
                                </dd>
                            </dl>
                        </fieldset>
                        <fieldset>
                            <legend>{t("eventTicketDetailsModal.customerDetails")}</legend>
                            <Form.Group className="mb-1" controlId="name">
                                <Form.Label className="mb-0">{t("eventTicketsPage.name")}</Form.Label>
                                <Form.Control type="text" name="name" required={false} value={eventTicketCustomerUpdate?.customer.name || ""} onChange={(e) => handleCustomerChange(e as any)} />
                                <Form.Control.Feedback type="invalid">{t("eventTicketsPage.requiredName")}</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="mb-1" controlId="tel">
                                <Form.Label className="mb-0">{t("eventTicketsPage.tel")}</Form.Label>
                                <PhoneInput
                                    country={'vn'}
                                    countryCodeEditable={false}
                                    placeholder=""
                                    searchPlaceholder={t("phoneInput.searchPlaceholder")}
                                    searchNotFound={t("phoneInput.searchNotFound")}
                                    enableSearch={true}
                                    value={eventTicketCustomerUpdate.customer.tel}
                                    onChange={handlePhoneInputChange}
                                />
                                {/* <Form.Control type="text" name="tel" required={false} value={eventTicketCustomerUpdate?.customer.tel || ""} onChange={(e) => handleCustomerChange(e as any)} />
                                <Form.Control.Feedback type="invalid">{t("eventTicketsPage.requiredTel")}</Form.Control.Feedback> */}
                            </Form.Group>
                            <Form.Group className="mb-1" controlId="email">
                                <Form.Label className="mb-0">Email</Form.Label>
                                <Form.Control type="email" name="email" required={false} value={eventTicketCustomerUpdate?.customer.email || ""} onChange={(e) => handleCustomerChange(e as any)} />
                                <Form.Control.Feedback type="invalid">{t("eventTicketsPage.requiredEmail")}</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="mb-1" controlId="address">
                                <Form.Label className="mb-0">{t("eventTicketsPage.address")}</Form.Label>
                                <Form.Control type="text" name="address" value={eventTicketCustomerUpdate?.customer.address || ""} onChange={(e) => handleCustomerChange(e as any)} />
                            </Form.Group>
                            <Form.Group className="mb-1" controlId="personalId">
                                <Form.Label className="mb-0">{t("eventTicketsPage.personalId")}</Form.Label>
                                <Form.Control type="text" name="personalId" value={eventTicketCustomerUpdate?.customer.personalId || ""} onChange={(e) => handleCustomerChange(e as any)} />
                                {/* <Form.Text>Priority custormer</Form.Text> */}
                            </Form.Group>
                        </fieldset>
                    </>}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                {message !== "" && <Alert variant="danger" onClose={() => setMessage("")} dismissible style={{ flex: "1" }}>{message}</Alert>}
                <Button variant="light" onClick={props.onClose}><i className="fas fa-times"></i> {t("common.cancel")}</Button>
                <Button variant="info" onClick={handleSave} disabled={processing}>
                    {processing ? <Spinner animation="border" size="sm" /> : <i className="fas fa-save"></i>} {t("common.save")}
                </Button>
            </Modal.Footer>
        </Modal>
        <QRCodeModal image={qrCode.image} code={qrCode.code} caption={qrCode.caption} show={showQRCodeModal} onClose={handleCloseQRCodeModal} onDownload={handleDownloadQRCode}></QRCodeModal>
    </>)
}

export default EventTicketCustomerEditModal